<script lang="ts" setup>
import Avatar from '@/components/Avatar.vue'
import SkrButton from '@/components/button/SkrButton.vue'
import Logo from '@/components/Logo.vue'
import SkrDivider from '@/components/SkrDivider.vue'

const emit = defineEmits<{
  change: [value: boolean]
}>()

const props = withDefaults(
  defineProps<{
    active?: boolean
  }>(),
  {
    active: false,
  }
)

const drawer = ref(false)

const route = useRoute()

const businessStore = useBusinessStore()
const { firstName, lastName, highestSignatureQuality, canCreateSignatureRequest } = storeToRefs(useUserStore())

const { isPlanPageFlag, isAdminAreaFlag } = useNavigationFlags()

const { createBlockedTooltip } = useCreateBlockedTooltip()

const { activePricePlanId, isNewFreePlan, isScalePlan } = useUserPlanInfo()
const systemStore = useSystemStore()

const displayedName = computed(() => `${firstName.value.split(' ')[0]} ${lastName.value}`)
const supportContact = computed(() => businessStore.settings.supportContact)
const brandingLogoUrl = computed(() => businessStore.settings.companyBranding?.logo.url ?? '')
const platform = computed(() => systemStore.platform || 'CH')
const { t } = useI18n()

const emailContactLink = computed(() => {
  if (supportContact.value?.email) {
    return `mailto:${supportContact.value.email}`
  }

  return platform.value === 'EU' ? t('global.navigation.contact.link_form_de') : t('global.navigation.contact.link')
})

const goToAdmin = computed(() => {
  if (['TRIAL', 'TEAM', 'PRO', 'SCALE'].some(plan => activePricePlanId.value?.startsWith(plan))) {
    return 'business-dashboard'
  } else if (activePricePlanId.value?.startsWith('INDIVIDUAL')) {
    return 'business-billing-plan'
  } else {
    return 'business-members'
  }
})

watch(drawer, val => {
  emit('change', val)
})

watch(
  () => props.active,
  val => {
    drawer.value = val
  }
)

const { isSupportContactFlag } = useNavigationFlags()

const { openFeaturebase } = useFeaturebaseSso()
const { $featurebase } = useNuxtApp()
</script>

<template>
  <!--
    TODO: This and MainNav.vue are very similar and should be
    refactored for less duplication.
    Maybe even consider MainNavBusiness.vue and MiniNavBusiness.vue too.
  -->
  <v-navigation-drawer v-model="drawer" class="main-nav" temporary :width="300">
    <div class="d-flex flex-column justify-space-between fill-height">
      <div>
        <nuxt-link to="/" class="text-no-decoration">
          <logo class="main-nav__logo my-5 mx-4" :image="brandingLogoUrl" />
        </nuxt-link>
        <template v-if="$vuetify.display.smAndDown && isNewFreePlan">
          <div class="px-6 pb-6">
            <p class="mb-4">{{ t('upgrade.free_indicator') }}</p>
            <skr-button size="lg" block to="/upgrade">{{ t('upgrade.choose_plan') }}</skr-button>
          </div>
          <skr-divider />
        </template>
        <v-list>
          <v-tooltip
            content-class="text-center bg-info"
            location="bottom"
            max-width="207"
            :disabled="canCreateSignatureRequest"
          >
            <template #activator="{ props: activatorProps }">
              <div v-bind="activatorProps">
                <v-list-item
                  :disabled="isNewFreePlan || !canCreateSignatureRequest"
                  :to="{ name: 'upload' }"
                  class="main-nav__add"
                  @click="drawer = false"
                >
                  <v-list-item-action>
                    <v-icon>custom:c_add_full</v-icon>
                  </v-list-item-action>

                  <v-list-item-title>
                    {{ t('global.navigation.new') }}
                  </v-list-item-title>
                </v-list-item>
              </div>
            </template>
            <span>{{ createBlockedTooltip }}</span>
          </v-tooltip>
          <v-list-item
            :to="{ name: 'index', query: { tab: 'inbox' } }"
            :active="route.name === 'index' && route.query.tab != 'drafts'"
            data-cy="main_navigation_inbox"
            @click="drawer = false"
          >
            <v-list-item-action>
              <v-icon>custom:folder_user</v-icon>
            </v-list-item-action>

            <v-list-item-title>
              {{ t('global.navigation.inbox') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            :to="{ name: 'index', query: { tab: 'drafts' } }"
            :active="route.query.tab === 'drafts'"
            data-cy="main_navigation_drafts"
            @click="drawer = false"
          >
            <v-list-item-action>
              <v-icon>custom:draft</v-icon>
            </v-list-item-action>

            <v-list-item-title>
              {{ t('global.navigation.drafts') }}
            </v-list-item-title>
          </v-list-item>
          <template v-if="isPlanPageFlag">
            <skr-divider />
            <v-list-item
              :to="{ name: 'plan' }"
              data-cy="main_navigation_plan"
              class="main-nav__plan"
              @click="drawer = false"
            >
              <v-list-item-action>
                <v-icon>custom:starter_plan</v-icon>
              </v-list-item-action>

              <v-list-item-title class="main-nav__plan__label">
                {{ t('global.navigation.my_plan') }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </div>
      <div>
        <v-divider />
        <v-list class="py-0">
          <v-list-item :to="{ name: 'logout' }" data-cy="logout_button">
            <v-list-item-action>
              <v-icon>custom:leave</v-icon>
            </v-list-item-action>

            <v-list-item-title>
              {{ t('global.navigation.logout') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
        <v-divider v-if="!isScalePlan" />
        <v-list v-if="!isScalePlan" class="py-0">
          <v-list-item @click="openFeaturebase()">
            <v-list-item-action>
              <v-icon>custom:feedback</v-icon>
            </v-list-item-action>

            <v-list-item-title>
              {{ t('feedback.featurebase.label') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item data-cy="changelog_button" data-featurebase-changelog>
            <v-list-item-action class="main-nav__changelog-action">
              <v-icon>custom:changelog</v-icon>
              <span v-if="$featurebase.unreadChangelogCount > 0" class="main-nav__changelog-badge">
                {{ $featurebase.unreadChangelogCount > 9 ? '9+' : $featurebase.unreadChangelogCount }}
              </span>
            </v-list-item-action>

            <v-list-item-title>
              {{ t('changelog.title') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
        <v-divider />
        <v-list class="main-nav__help-overview py-0">
          <v-list-group value="Help">
            <template #activator="{ props: activatorProps }">
              <v-list-item v-bind="activatorProps" class="px-0">
                <v-list-item-action>
                  <v-icon>custom:c_question</v-icon>
                </v-list-item-action>

                <v-list-item-title>
                  {{ t('global.navigation.help_center') }}
                </v-list-item-title>
              </v-list-item>
            </template>

            <v-list-item :href="t('global.navigation.helpcenter.link')" target="_blank" rel="noopener">
              <v-list-item-action>
                <v-icon>custom:help_center</v-icon>
              </v-list-item-action>

              <v-list-item-title>
                {{ t('global.navigation.helpcenter.label') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item :href="t('global.navigation.tutorials.link')" target="_blank" rel="noopener">
              <v-list-item-action>
                <v-icon>custom:video_tutorials</v-icon>
              </v-list-item-action>

              <v-list-item-title>
                {{ t('global.navigation.tutorials.label') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isSupportContactFlag" :href="emailContactLink" target="_blank" rel="noopener">
              <v-list-item-action>
                <v-icon>custom:contact_email</v-icon>
              </v-list-item-action>

              <v-list-item-title>
                {{
                  supportContact?.email
                    ? t('global.navigation.internal_support_contact.mail.label')
                    : t('global.navigation.contact.label')
                }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="supportContact?.phone"
              :href="`tel:${supportContact.phone}`"
              target="_blank"
              rel="noopener"
            >
              <v-list-item-action>
                <v-icon>custom:support_hotline</v-icon>
              </v-list-item-action>
              <v-list-item-title class="pb-1">
                {{ t('global.navigation.call.label') }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ supportContact.phone }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list-group>
        </v-list>
        <template v-if="isAdminAreaFlag">
          <v-divider />
          <v-list class="py-0">
            <v-list-item :exact="true" :to="{ name: goToAdmin }" @click="drawer = false">
              <v-list-item-action>
                <v-icon>custom:business_account</v-icon>
              </v-list-item-action>

              <v-list-item-title>
                {{ t('global.navigation.admin_area') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </template>
        <v-divider />
        <v-list class="py-0">
          <v-list-item :to="{ name: 'profile' }" @click="drawer = false">
            <v-avatar class="mr-5 my-2">
              <avatar />
              <nuxt-img
                v-if="!highestSignatureQuality"
                width="16"
                height="16"
                class="main-nav__signing-status"
                src="/img/info-warning-small.svg"
                alt=""
              />
            </v-avatar>

            <v-list-item-title>{{ displayedName }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<style lang="scss">
.main-nav {
  &__add {
    .v-list-item-action,
    .v-list-item-title {
      color: $c-primary;
    }
  }

  .version {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    color: $c-text;
    font-size: 0.75rem;
  }

  .v-avatar {
    position: relative;
    overflow: visible;
  }

  .v-list-item {
    padding: 0 22px !important;
    font-size: 0.875rem;
    line-height: 1.1428;
    font-weight: bold;

    &.v-theme--light {
      &:not(.v-list-item--active) {
        &:hover {
          background: rgba(151, 151, 151, 0.1);
        }
        &:not(.main-nav__add) {
          .v-icon,
          .v-list-item-title {
            color: $c-grey-dark;
          }
        }
      }
      &.v-list-item--active {
        &:hover {
          .v-list-item__overlay {
            opacity: 0.12;
          }
          background: inherit;
        }
      }
    }

    .v-list-item__sub-title {
      color: $c-text-alt;
    }

    .v-avatar {
      margin-left: -6px;
    }

    &__title {
      font-size: 0.875rem;
      line-height: 1.1428;
    }

    &--active {
      color: $c-primary;
      .v-icon,
      .v-list-item__content {
        color: $c-primary;
      }

      &:before {
        background: none;
      }
    }

    &--disabled,
    &--disabled span {
      color: #d8d8d8;
    }

    .v-list-item-action {
      margin: 12px 32px 12px 0;
    }

    .v-list-item__content {
      display: flex;
      align-items: center;
      overflow: visible;
    }
  }

  .v-list-group__header {
    .v-list-item {
      &:hover {
        background: none;
      }
    }
  }

  .v-card__title {
    small {
      font-size: 0.875rem;
      color: $c-text;
    }
  }

  .main-nav__signing-status {
    position: absolute;
    right: -4px;
    top: -4px;
    width: 16px;
    height: 16px;
  }

  &__help-overview {
    .v-list__group:before,
    .v-list__group:after {
      display: none;
    }

    .v-list__group__header--active {
      color: $c-grey-dark;
      border: none;
      border-left: 4px solid $c-grey-dark;

      .v-list-item .v-theme--light,
      .v-list-item__content {
        transition: none;
        margin-left: -4px;
      }

      svg,
      i {
        color: $c-grey-dark;
      }
    }

    .v-list-item__sub-title {
      color: inherit !important;
    }
  }

  &__changelog-action {
    position: relative;
  }

  &__changelog-badge {
    position: absolute;
    top: 6px;
    right: 24px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    color: $c-primary;
    background-color: $c-primary-bg;
    border-radius: 4px;
  }

  &__plan {
    &.v-list-item {
      &:hover {
        > .v-list-item__overlay {
          background: $c-purple-bg;
        }
      }
      &--active {
        & .v-list-item__content,
        .v-icon {
          color: $c-purple-dark;
        }
        > .v-list-item__overlay {
          background: $c-purple-bg;
          opacity: 1;
          transition: background-color 0.3s ease;
        }
        &:hover {
          > .v-list-item__overlay {
            background: $c-purple-bg;
            opacity: 1 !important;
          }
          > .v-list-item__content,
          .v-icon {
            color: $c-purple-dark;
          }
        }
      }
    }
    &__label {
      z-index: 1;
    }
  }
}
</style>
